import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, Mail, Phone, CheckCircle2, Star, ExternalLink } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

export default function Jobs() {
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-navy text-cream py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 text-xs tracking-widest text-sponsored uppercase mb-3">
            <Briefcase className="w-4 h-4" /> Local Jobs
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <h1 className="font-heading text-4xl lg:text-6xl text-cream tracking-tight">
                Work in the <em className="text-sponsored">Whitsundays</em>
              </h1>
              <p className="text-cream/70 mt-3 max-w-2xl">
                Real openings from local operators. Sail crews, hospitality, dive, retail — the
                jobs that keep Airlie running.
              </p>
            </div>
            <a
              href="https://eazyjobs.app"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => api.post("/sponsor-clicks", { sponsor: "eazy_jobs", surface: "web_jobs_hero", target_url: "https://eazyjobs.app" }).catch(() => {})}
              className="inline-flex items-center gap-2 text-xs tracking-widest text-cream/70 uppercase hover:text-sponsored"
              data-testid="eazy-jobs-sponsor"
            >
              Sponsored by <span className="font-heading text-cream text-base normal-case tracking-normal">Eazy Jobs</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* List */}
      <section className="bg-cream py-16">
        <div className="max-w-4xl mx-auto px-6">
          {jobs === null ? (
            <div className="text-neutral-500">Loading…</div>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 border border-black/5 text-center">
              <Briefcase className="w-8 h-8 text-navy/40 mx-auto mb-3" />
              <h2 className="font-heading text-2xl text-navy">No open roles today</h2>
              <p className="text-neutral-500 mt-2">Check back soon — new roles land here weekly.</p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="jobs-list">
              {jobs.map((job, i) => (
                <motion.article
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  className="bg-white rounded-2xl border border-black/5 p-6 lg:p-8"
                  data-testid={`job-${job.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] tracking-widest text-coastal uppercase">
                        {job.employer || "Local employer"} · {job.location || "Airlie Beach"}
                      </div>
                      <h2 className="font-heading text-2xl lg:text-3xl text-navy mt-1">
                        {job.title}
                      </h2>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {job.employment_type && (
                          <span className="text-xs bg-cream text-navy px-3 py-1 rounded-full">
                            {job.employment_type}
                          </span>
                        )}
                        {job.salary_note && (
                          <span className="text-xs bg-cream text-navy px-3 py-1 rounded-full">
                            {job.salary_note}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-neutral-700 leading-relaxed mt-4">{job.description}</p>

                  {job.qualifications?.length ? (
                    <div className="mt-5">
                      <h3 className="text-[10px] tracking-widest text-coastal uppercase mb-2">
                        Required
                      </h3>
                      <ul className="space-y-1.5">
                        {job.qualifications.map((q, k) => (
                          <li key={k} className="flex gap-2 text-sm text-navy">
                            <CheckCircle2 className="w-4 h-4 text-coastal mt-0.5 flex-shrink-0" />
                            <span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {job.preferred?.length ? (
                    <div className="mt-4">
                      <h3 className="text-[10px] tracking-widest text-coastal uppercase mb-2">
                        Nice to have
                      </h3>
                      <ul className="space-y-1.5">
                        {job.preferred.map((q, k) => (
                          <li key={k} className="flex gap-2 text-sm text-neutral-600">
                            <Star className="w-4 h-4 text-sponsored mt-0.5 flex-shrink-0" />
                            <span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div className="mt-6 pt-5 border-t border-black/5 flex flex-wrap gap-3">
                    {job.contact_email && (
                      <a
                        href={`mailto:${job.contact_email}`}
                        className="inline-flex items-center gap-2 bg-navy text-cream px-5 py-2.5 rounded-full text-sm font-medium hover:bg-navy/90"
                        data-testid={`job-email-${job.id}`}
                      >
                        <Mail className="w-4 h-4" /> Apply by email
                      </a>
                    )}
                    {job.contact_phone && (
                      <a
                        href={`tel:${job.contact_phone.replace(/\s/g, "")}`}
                        className="inline-flex items-center gap-2 border border-navy text-navy px-5 py-2.5 rounded-full text-sm font-medium hover:bg-navy hover:text-cream"
                        data-testid={`job-phone-${job.id}`}
                      >
                        <Phone className="w-4 h-4" /> {job.contact_phone}
                      </a>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
